import type { PermissionMode } from '@omni/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../app/AppShell';
import { useSession } from '../app/SessionProvider';
import {
  useChangePassword,
  useEmployee,
  useLogout,
  useMcpServers,
  usePatchEmployee,
  usePutMcpServers,
  useRestartRunner,
  useRevokeToolGrant,
  useRunner,
  useToolGrants,
  useUsage,
} from '../api/hooks';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Select,
  Sheet,
  Spinner,
  TextInput,
} from '../components/ui';
import { zh } from '../i18n';
import { formatMb, formatRelative } from '../lib/format';
import { isStandalone, promptInstall, watchInstallPrompt } from '../lib/pwa';
import { BudgetSection } from './BudgetSection';
import { McpEditor } from './McpEditor';

/**
 * 設定 (SPEC §10): 員工名稱 · 模型 · 權限模式 · 費用上限 · MCP · 自動核准清單 ·
 * 容器狀態 · 修改密碼 · 安裝 App.
 */

const PERMISSION_MODES: readonly { value: PermissionMode; label: string }[] = [
  { value: 'default', label: zh.settings.permissionModeDefault },
  { value: 'acceptEdits', label: zh.settings.permissionModeAcceptEdits },
  { value: 'plan', label: zh.settings.permissionModePlan },
];

export function SettingsRoute(): JSX.Element {
  const employee = useEmployee();
  const patchEmployee = usePatchEmployee();
  const usage = useUsage();
  const mcp = useMcpServers();
  const putMcp = usePutMcpServers();
  const grants = useToolGrants();
  const revokeGrant = useRevokeToolGrant();
  const runner = useRunner();
  const restartRunner = useRestartRunner();
  const changePassword = useChangePassword();
  const logout = useLogout();
  const { runnerState, me } = useSession();

  const [displayName, setDisplayName] = useState('');
  const [model, setModel] = useState('');
  const [permissionMode, setPermissionMode] = useState<PermissionMode>('default');
  const [installAvailable, setInstallAvailable] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordDone, setPasswordDone] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!employee.data) return;
    setDisplayName(employee.data.displayName);
    setModel(employee.data.model);
    setPermissionMode(employee.data.permissionMode);
  }, [employee.data]);

  useEffect(() => watchInstallPrompt(setInstallAvailable), []);

  const config = employee.data;

  const submitPassword = (event: FormEvent): void => {
    event.preventDefault();
    setPasswordDone(false);
    if (newPassword.length < 12) {
      setPasswordError(zh.firstRun.tooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(zh.firstRun.mismatch);
      return;
    }
    setPasswordError(null);
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setPasswordDone(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
      },
    );
  };

  return (
    <AppShell>
      <div className="page-head">
        <h1 className="page-head__title">{zh.settings.title}</h1>
      </div>

      {employee.isLoading ? <Spinner /> : null}
      <ErrorText error={employee.error} />

      {config ? (
        <div className="stack stack--lg">
          <Card>
            <h2>{zh.settings.sectionEmployee}</h2>
            <div className="stack">
              <Field label={zh.settings.displayName} hint={zh.settings.displayNameHint}>
                {(id) => (
                  <TextInput
                    id={id}
                    value={displayName}
                    maxLength={40}
                    onChange={(event) => setDisplayName(event.target.value)}
                  />
                )}
              </Field>
              <Button
                variant="primary"
                disabled={patchEmployee.isPending || displayName === config.displayName}
                onClick={() => patchEmployee.mutate({ displayName })}
              >
                {patchEmployee.isPending ? zh.app.saving : zh.app.save}
              </Button>
            </div>
          </Card>

          <Card>
            <h2>{zh.settings.sectionModel}</h2>
            <div className="stack">
              <Field label={zh.settings.model}>
                {(id) => (
                  <Select
                    id={id}
                    value={model}
                    onChange={(event) => setModel(event.target.value)}
                  >
                    {config.modelAllowlist.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={zh.settings.permissionMode} hint={zh.settings.permissionModeHint}>
                {(id) => (
                  <Select
                    id={id}
                    value={permissionMode}
                    onChange={(event) => setPermissionMode(event.target.value as PermissionMode)}
                  >
                    {PERMISSION_MODES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <ErrorText error={patchEmployee.error} />
              <Button
                variant="primary"
                disabled={
                  patchEmployee.isPending ||
                  (model === config.model && permissionMode === config.permissionMode)
                }
                onClick={() =>
                  patchEmployee.mutate({
                    model: model as (typeof config.modelAllowlist)[number],
                    permissionMode,
                  })
                }
              >
                {patchEmployee.isPending ? zh.app.saving : zh.app.save}
              </Button>
            </div>
          </Card>

          <BudgetSection
            employee={config}
            usage={usage.data ?? null}
            saving={patchEmployee.isPending}
            onSave={(patch) => patchEmployee.mutate(patch)}
          />

          <McpEditor
            servers={mcp.data}
            loading={mcp.isLoading}
            saving={putMcp.isPending}
            saveError={putMcp.error}
            onSave={(servers) => putMcp.mutate(servers)}
          />

          <Card>
            <h2>{zh.settings.sectionGrants}</h2>
            <p className="small muted">{zh.settings.grantsHint}</p>
            {grants.isLoading ? <Spinner /> : null}
            {grants.data && grants.data.length === 0 ? (
              <p className="small muted">{zh.settings.grantsEmpty}</p>
            ) : null}
            {grants.data && grants.data.length > 0 ? (
              <ul className="list">
                {grants.data.map((grant) => (
                  <li key={grant.id} className="row row--between" style={{ padding: '0.4rem 0' }}>
                    <div className="list__main">
                      <div className="list__title">{grant.label}</div>
                      <div className="list__sub">
                        {grant.useCount > 0
                          ? zh.settings.grantsUsed(grant.useCount)
                          : zh.settings.grantsNeverUsed}
                        {grant.lastUsedAt ? ` · ${formatRelative(grant.lastUsedAt)}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      small
                      disabled={revokeGrant.isPending}
                      onClick={() => revokeGrant.mutate(grant.toolName)}
                    >
                      {zh.settings.grantsRevoke}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            {revokeGrant.isSuccess ? (
              <p className="small muted">{zh.settings.grantsRevoked}</p>
            ) : null}
          </Card>

          <Card>
            <h2>{zh.settings.sectionRunner}</h2>
            {runner.isLoading ? <Spinner /> : null}
            {runner.data ? (
              <div className="stack">
                <div className="kv">
                  <span className="kv__k">{zh.settings.runnerState}</span>
                  <span>
                    <Badge tone={runner.data.state === 'error' ? 'danger' : 'muted'}>
                      {zh.runnerState[runner.data.state]}
                    </Badge>
                  </span>
                  <span className="kv__k">{zh.settings.runnerVolume}</span>
                  <span>
                    {`${formatMb(runner.data.volume.usedMb)} / ${runner.data.volume.hardLimitMb}`}
                  </span>
                  <span className="kv__k">{zh.settings.runnerMounts}</span>
                  <span>
                    {runner.data.mounts.length === 0
                      ? zh.settings.runnerNoMounts
                      : runner.data.mounts
                          .map((mount) =>
                            zh.settings.runnerMountEntry(
                              mount.slug,
                              mount.mode === 'rw' ? zh.spaces.levelRw : zh.spaces.levelRo,
                            ),
                          )
                          .join(' · ')}
                  </span>
                </div>
                <p className="tiny faint">
                  {zh.settings.runnerLimits(
                    runner.data.limits.memoryMb,
                    (runner.data.limits.cpuMillis / 1000).toFixed(1),
                    runner.data.limits.pidsLimit,
                  )}
                </p>
                {runner.data.volume.usedMb > runner.data.volume.softLimitMb ? (
                  <div className="banner banner--warn">
                    <span className="banner__text">
                      {zh.settings.volumeWarning(formatMb(runner.data.volume.usedMb))}
                    </span>
                  </div>
                ) : null}
                <ErrorText error={restartRunner.error} />
                <Button
                  variant="outline"
                  disabled={restartRunner.isPending}
                  onClick={() => {
                    // A restart during a live turn interrupts it, so that case
                    // asks first instead of silently discarding work.
                    if (runner.data?.activeTurnId) setConfirmRestart(true);
                    else restartRunner.mutate({ force: false });
                  }}
                >
                  {restartRunner.isPending
                    ? zh.settings.runnerRestarting
                    : zh.settings.runnerRestart}
                </Button>
              </div>
            ) : (
              <p className="small muted">{runnerState ? zh.runnerState[runnerState] : ''}</p>
            )}
          </Card>

          <Card>
            <h2>{zh.settings.sectionPassword}</h2>
            <form className="stack" onSubmit={submitPassword}>
              <Field label={zh.settings.currentPassword}>
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                  />
                )}
              </Field>
              <Field label={zh.settings.newPassword}>
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                )}
              </Field>
              <Field label={zh.settings.confirmPassword} error={passwordError}>
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                )}
              </Field>
              <ErrorText error={changePassword.error} />
              {passwordDone ? (
                <p className="small muted">{zh.settings.passwordChanged}</p>
              ) : null}
              <Button variant="primary" type="submit" disabled={changePassword.isPending}>
                {changePassword.isPending ? zh.app.saving : zh.settings.changePassword}
              </Button>
            </form>
          </Card>

          <Card>
            <h2>{zh.settings.sectionAccount}</h2>
            <div className="kv">
              <span className="kv__k">{zh.login.email}</span>
              <span>{me?.user.email ?? ''}</span>
            </div>
            <Button
              variant="ghost"
              disabled={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })}
            >
              {zh.app.logout}
            </Button>
          </Card>

          <Card>
            <h2>{zh.settings.sectionInstall}</h2>
            <p className="small muted">{zh.settings.installHint}</p>
            {isStandalone() ? (
              <p className="small">{zh.settings.installed}</p>
            ) : installAvailable ? (
              <Button variant="outline" onClick={() => void promptInstall()}>
                {zh.settings.installAction}
              </Button>
            ) : (
              <p className="small muted">{zh.settings.installIosHint}</p>
            )}
          </Card>
        </div>
      ) : null}

      {confirmRestart ? (
        <Sheet title={zh.settings.runnerRestart} onClose={() => setConfirmRestart(false)}>
          <p className="small">{zh.settings.runnerRestartBusy}</p>
          <div className="row">
            <Button
              variant="primary"
              onClick={() => {
                setConfirmRestart(false);
                restartRunner.mutate({ force: true });
              }}
            >
              {zh.settings.runnerRestartForce}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmRestart(false)}>
              {zh.app.cancel}
            </Button>
          </div>
        </Sheet>
      ) : null}
    </AppShell>
  );
}
